import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import useApi from '../../hooks/useApi';

const RecipientLine = ({ label, values }) => (
  <div>
    <Typography variant="subtitle2">{label}</Typography>
    <Typography variant="body2">
      {Array.isArray(values) && values.length ? values.join(', ') : '—'}
    </Typography>
  </div>
);

const EventEmailOperationDialog = ({
  row,
  operation,
  onClose,
  onCompleted,
}) => {
  const {
    usePreviewEventEmailOperation,
    useCommitEventEmailOperation,
  } = useApi();
  const previewOperation = usePreviewEventEmailOperation();
  const commitOperation = useCommitEventEmailOperation();
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const idempotencyKeyRef = useRef(null);
  const committingRef = useRef(false);

  useEffect(() => {
    let active = true;
    setPreview(null);
    setReason('');
    setError('');
    idempotencyKeyRef.current = null;
    committingRef.current = false;
    if (!row?.id || !operation) return () => { active = false; };
    previewOperation.mutateAsync({
      id: row.id,
      operation,
      eventFamily: row.eventFamily,
    }).then((result) => {
      if (active) {
        idempotencyKeyRef.current = crypto.randomUUID();
        setPreview(result);
      }
    }).catch((requestError) => {
      if (active) {
        setError(
          requestError?.response?.data?.reason ||
          'This event email is not eligible for the requested action.'
        );
      }
    });
    return () => { active = false; };
    // Mutation functions are stable for the life of this dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.id, row?.eventFamily, operation]);

  const commit = async () => {
    if (
      !preview ||
      !reason.trim() ||
      committingRef.current ||
      !idempotencyKeyRef.current
    ) return;
    committingRef.current = true;
    setError('');
    try {
      await commitOperation.mutateAsync({
        id: row.id,
        operation,
        eventFamily: row.eventFamily,
        approvalToken: preview.approvalToken,
        reason: reason.trim(),
        idempotencyKey: idempotencyKeyRef.current,
        assetFingerprint: preview.assetFingerprint,
        renderFingerprint: preview.renderFingerprint,
      });
      onCompleted?.();
      onClose?.();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.reason ||
        'The approved event email action could not be completed.'
      );
    } finally {
      committingRef.current = false;
    }
  };

  return (
    <Dialog open={Boolean(row && operation)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {operation === 'resend' ? 'Resend event email' : 'Retry failed event email'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            This action uses the current saved template and recipient routing.
            The original send evidence will remain unchanged.
          </Alert>
          {error && <Alert severity="error">{error}</Alert>}
          {!preview && !error && <Typography>Preparing preview…</Typography>}
          {preview && (
            <>
              {preview.duplicateRisk && (
                <Alert severity="warning">
                  The prior provider outcome is uncertain. Sending again may
                  deliver a duplicate email.
                </Alert>
              )}
              <RecipientLine label="To" values={preview.recipients?.to} />
              <RecipientLine label="CC" values={preview.recipients?.cc} />
              <RecipientLine label="BCC" values={preview.recipients?.bcc} />
              <Typography variant="body2">
                Billing End Date: {preview.billingEndDate || '—'}
              </Typography>
              <TextField
                label="Reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
                required
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={commit}
          disabled={!preview || !reason.trim() || commitOperation.isPending}
        >
          {commitOperation.isPending
            ? 'Sending…'
            : operation === 'resend'
              ? 'Resend'
              : 'Retry'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventEmailOperationDialog;
